export interface Rgba {
	r: number
	g: number
	b: number
	a: number
}

const NAMED_COLORS: Record<string, [number, number, number]> = {
	aqua: [0, 255, 255],
	black: [0, 0, 0],
	blue: [0, 0, 255],
	brown: [165, 42, 42],
	crimson: [220, 20, 60],
	cyan: [0, 255, 255],
	darkblue: [0, 0, 139],
	darkgray: [169, 169, 169],
	darkgreen: [0, 100, 0],
	darkgrey: [169, 169, 169],
	darkred: [139, 0, 0],
	dimgray: [105, 105, 105],
	dimgrey: [105, 105, 105],
	fuchsia: [255, 0, 255],
	gainsboro: [220, 220, 220],
	gold: [255, 215, 0],
	gray: [128, 128, 128],
	green: [0, 128, 0],
	grey: [128, 128, 128],
	indigo: [75, 0, 130],
	lightgray: [211, 211, 211],
	lightgrey: [211, 211, 211],
	lime: [0, 255, 0],
	magenta: [255, 0, 255],
	maroon: [128, 0, 0],
	navy: [0, 0, 128],
	olive: [128, 128, 0],
	orange: [255, 165, 0],
	pink: [255, 192, 203],
	purple: [128, 0, 128],
	red: [255, 0, 0],
	royalblue: [65, 105, 225],
	silver: [192, 192, 192],
	teal: [0, 128, 128],
	violet: [238, 130, 238],
	white: [255, 255, 255],
	whitesmoke: [245, 245, 245],
	yellow: [255, 255, 0],
}

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value))

const hueToRgb = (p: number, q: number, tRaw: number) => {
	let t = tRaw
	if (t < 0) t += 1
	if (t > 1) t -= 1
	if (t < 1 / 6) return p + (q - p) * 6 * t
	if (t < 1 / 2) return q
	if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
	return p
}

const hslToRgb = (
	h: number,
	s: number,
	l: number,
): [number, number, number] => {
	if (s === 0) {
		const v = Math.round(l * 255)
		return [v, v, v]
	}
	const q = l < 0.5 ? l * (1 + s) : l + s - l * s
	const p = 2 * l - q
	const hk = (((h % 360) + 360) % 360) / 360
	return [
		Math.round(hueToRgb(p, q, hk + 1 / 3) * 255),
		Math.round(hueToRgb(p, q, hk) * 255),
		Math.round(hueToRgb(p, q, hk - 1 / 3) * 255),
	]
}

export const parseCssColor = (raw: string): Rgba | null => {
	const value = raw.trim().toLowerCase()
	if (!value) return null
	if (value === "transparent") return { r: 0, g: 0, b: 0, a: 0 }

	const named = NAMED_COLORS[value]
	if (named) return { r: named[0], g: named[1], b: named[2], a: 1 }

	const hex = value.match(/^#([0-9a-f]{3,8})$/)
	if (hex?.[1]) {
		const h = hex[1]
		const expand = (c: string) => parseInt(c + c, 16)
		if (h.length === 3 || h.length === 4) {
			return {
				r: expand(h[0]!),
				g: expand(h[1]!),
				b: expand(h[2]!),
				a: h.length === 4 ? expand(h[3]!) / 255 : 1,
			}
		}
		if (h.length === 6 || h.length === 8) {
			return {
				r: parseInt(h.slice(0, 2), 16),
				g: parseInt(h.slice(2, 4), 16),
				b: parseInt(h.slice(4, 6), 16),
				a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
			}
		}
		return null
	}

	const rgb = value.match(
		/^rgba?\(\s*(\d+(?:\.\d+)?%?)[,\s]+(\d+(?:\.\d+)?%?)[,\s]+(\d+(?:\.\d+)?%?)\s*(?:[,/]\s*(\d*\.?\d+)%?\s*)?\)$/,
	)
	if (rgb) {
		const channel = (input: string) =>
			input.endsWith("%")
				? Math.round((parseFloat(input) / 100) * 255)
				: Math.round(parseFloat(input))
		return {
			r: clamp(channel(rgb[1]!), 0, 255),
			g: clamp(channel(rgb[2]!), 0, 255),
			b: clamp(channel(rgb[3]!), 0, 255),
			a: rgb[4] === undefined ? 1 : clamp(parseFloat(rgb[4]), 0, 1),
		}
	}

	const hsl = value.match(
		/^hsla?\(\s*(-?\d+(?:\.\d+)?)(?:deg)?[,\s]+(\d+(?:\.\d+)?)%[,\s]+(\d+(?:\.\d+)?)%\s*(?:[,/]\s*(\d*\.?\d+)%?\s*)?\)$/,
	)
	if (hsl) {
		const [r, g, b] = hslToRgb(
			parseFloat(hsl[1]!),
			clamp(parseFloat(hsl[2]!) / 100, 0, 1),
			clamp(parseFloat(hsl[3]!) / 100, 0, 1),
		)
		return {
			r,
			g,
			b,
			a: hsl[4] === undefined ? 1 : clamp(parseFloat(hsl[4]), 0, 1),
		}
	}

	return null
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export const relativeLuminance = ({ r, g, b }: Rgba): number => {
	const channel = (value: number) => {
		const c = value / 255
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
	}
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** HSL saturation, 0 (grey) to 1 (fully saturated). */
export const saturation = ({ r, g, b }: Rgba): number => {
	const max = Math.max(r, g, b) / 255
	const min = Math.min(r, g, b) / 255
	if (max === min) return 0
	const lightness = (max + min) / 2
	return lightness > 0.5
		? (max - min) / (2 - max - min)
		: (max - min) / (max + min)
}

const NEUTRAL_SATURATION = 0.12

/** Greys, near-black and near-white: colors that carry no brand intent. */
export const isNeutralColor = (color: Rgba): boolean =>
	color.a < 0.1 || saturation(color) < NEUTRAL_SATURATION

export const formatCssColor = ({ r, g, b, a }: Rgba): string =>
	a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`

const DARK_MODE_MIN_LUMINANCE = 0.2
const LIGHT_MODE_MAX_LUMINANCE = 0.55

const mix = (color: Rgba, target: Rgba, amount: number): Rgba => ({
	r: Math.round(color.r + (target.r - color.r) * amount),
	g: Math.round(color.g + (target.g - color.g) * amount),
	b: Math.round(color.b + (target.b - color.b) * amount),
	a: color.a,
})

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 }
const BLACK: Rgba = { r: 0, g: 0, b: 0, a: 1 }

/**
 * Nudges a branded text color toward the theme background until it is legible,
 * keeping its hue so the sender's accent survives.
 */
export const adaptTextColor = (color: Rgba, isDark: boolean): Rgba => {
	const target = isDark ? WHITE : BLACK
	const limit = isDark ? DARK_MODE_MIN_LUMINANCE : LIGHT_MODE_MAX_LUMINANCE
	let adapted = color
	for (let step = 0; step < 8; step++) {
		const luminance = relativeLuminance(adapted)
		const legible = isDark ? luminance >= limit : luminance <= limit
		if (legible) break
		adapted = mix(adapted, target, 0.2)
	}
	return adapted
}
